import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DescriptionIcon from "@mui/icons-material/Description";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import { Folder as FolderIcon } from "@mui/icons-material";

import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import { detectFileType } from "../../../utils/fileTypes";
import { truncateText } from "../../../utils/truncateText";

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAYS_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * Folder view component for patrols displayed in hierarchical groups
 */
export function PatrolFolderView({
  patrols = [],
  selectedPatrolId,
  onPatrolClick,
  onActionClick,
  groupBy = "drawingPath",
  onCopy,
  isComparePatrol,
  /** Background for non-selected session rows (Compare Patrol uses panel tint). */
  sessionListSurfaceColor = "white",
}) {
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  const truncateSx = {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flexShrink: 1,
  };

  const toggleFolder = (folderPath) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  };

  function parseDateParts(dateString) {
    if (!dateString) return null;

    const rawValue = String(dateString).trim();
    const timezoneStripped = rawValue
      .replace(/[zZ]$/, "")
      .replace(/([+-]\d{2}:?\d{2})$/, "");
    const match = timezoneStripped.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/,
    );

    if (!match) return null;

    return {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
      hour: Number(match[4] ?? 0),
      minute: Number(match[5] ?? 0),
      second: Number(match[6] ?? 0),
      hasTime: Boolean(match[4] && match[5]),
      hasSecond: Boolean(match[6]),
    };
  }

  function to12HourParts(hour) {
    const hour12 = ((hour + 11) % 12) + 1;
    const meridiem = hour >= 12 ? "PM" : "AM";
    return { hour12, meridiem };
  }

  function formatDate(dateString) {
    const parts = parseDateParts(dateString);
    if (!parts) return "";

    const month = MONTHS_SHORT[parts.month - 1];
    if (!month) return "";

    if (!parts.hasTime) {
      return `${month} ${parts.day}, ${parts.year}`;
    }

    const { hour12, meridiem } = to12HourParts(parts.hour);
    const minute = String(parts.minute).padStart(2, "0");
    return `${month} ${parts.day}, ${parts.year}, ${hour12}:${minute} ${meridiem}`;
  }

  function formatFullDate(dateString) {
    const parts = parseDateParts(dateString);
    if (!parts) return "";

    const month = MONTHS_LONG[parts.month - 1];
    if (!month) return "";

    const weekday =
      WEEKDAYS_LONG[
        new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()
      ];

    if (!parts.hasTime) {
      return `${weekday}, ${month} ${parts.day}, ${parts.year}`;
    }

    const { hour12, meridiem } = to12HourParts(parts.hour);
    const minute = String(parts.minute).padStart(2, "0");
    const second = String(parts.second).padStart(2, "0");

    if (!parts.hasSecond) {
      return `${weekday}, ${month} ${parts.day}, ${parts.year}, ${hour12}:${minute} ${meridiem}`;
    }

    return `${weekday}, ${month} ${parts.day}, ${parts.year}, ${hour12}:${minute}:${second} ${meridiem}`;
  }

  function formatPatrolId(patrolId) {
    if (!patrolId && patrolId !== 0) return "";
    const num = typeof patrolId === "string" ? parseInt(patrolId) : patrolId;
    return String(num).padStart(3, "0");
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflow: "auto",
        p: 2,
        minWidth: 0,
      }}
    >
      {patrols &&
        patrols.length > 0 &&
        patrols.map((patrol, index) => {
          let folderName = patrol?.path || patrol?.filename;
          let patrolList = patrol?.sessions || [];
          const folderPath =
            patrol?.path || patrol?.filename || `folder-${index}`;
          const isExpanded = expandedFolders.has(folderPath);

          const handleClick = async (text, type) => {
            // e.stopPropagation();
            if (!text) return;

            try {
              await navigator.clipboard.writeText(text);
              toast.success(`${type} copied to clipboard!`, {
                duration: 2000,
                icon: "📋",
              });

              onCopy && onCopy(text);
            } catch {
              toast.error(`Failed to copy ${type}!`, {
                duration: 2000,
              });
            }
          };

          function isFolderName(name) {
            return !/\.[^/.]+$/.test(name);
          }
          function getPathOnly(filePath) {
            const idx = filePath.lastIndexOf("/");
            return idx !== -1 ? filePath.slice(0, idx) : "";
          }

          return (
            <Box
              key={patrol?.path}
              sx={{
                mb: 2,
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {/* Folder Header */}
              <Box
                onClick={() => toggleFolder(folderPath)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.5,
                  bgcolor: "rgb(250, 250, 250)",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "rgb(255, 224, 208)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {groupBy === "drawingPath" ? (
                    <FolderIcon sx={{ fontSize: 20, color: "#2932e5" }} />
                  ) : (
                    <DescriptionIcon sx={{ fontSize: 20, color: "#2932e5" }} />
                  )}
                  <Typography
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "16px",
                      fontWeight: 400,
                      color: "#FF8555",
                    }}
                  >
                    {folderName}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={`${patrolList.length} ${patrolList.length === 1 ? "patrol" : "patrols"}`}
                    size="small"
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "12px",
                      fontWeight: 600,
                      bgcolor: "#F3F4F6",
                      color: "#4A5565",
                      height: "24px",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFolder(folderPath);
                    }}
                    sx={{
                      color: "rgb(41, 50, 229)",
                      padding: "4px",
                      "&:hover": {
                        bgcolor: "rgb(255, 224, 208)",
                      },
                    }}
                  >
                    {isExpanded ? (
                      <ExpandLessIcon sx={{ fontSize: "24px" }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: "24px" }} />
                    )}
                  </IconButton>
                </Box>
              </Box>

              {/* Folder Content */}
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                {patrolList &&
                  patrolList.length > 0 &&
                  patrolList.map((patrolItem, index) => {
                    let date = formatDate(patrolItem?.created_at);
                    let id = patrolItem?.session_id
                      ? patrolItem?.session_id
                      : "N/A";
                    let fullDate = formatFullDate(patrolItem?.created_at);
                    const rawPatrolId =
                      patrolItem?.id ?? patrolItem?.session_id ?? null;
                    const displayPatrolId =
                      rawPatrolId !== null && rawPatrolId !== undefined
                        ? `P-${rawPatrolId}`
                        : "N/A";

                    let path = patrolItem?.recordings?.[0]?.path;
                    let filename = path ? path.split("/").pop() : "";
                    let sessionName = `S-${String(patrolItem?.recordings?.[0]?.session_name?.split("_")[1]).padStart(3, "0")}`;
                    const isSelected =
                      selectedPatrolId === patrolItem?.session_id;

                    return (
                      <Box key={patrolList?.recordings?.[index]?.created_at}>
                        <Box
                          // key={123}
                          onClick={() =>
                            onPatrolClick?.(
                              patrolItem?.recordings || [],
                              patrolItem?.session_id,
                              patrolItem?.id ??
                                patrolItem?.patrol_id ??
                                patrolItem?.session_id,
                              patrolItem?.created_at,
                            )
                          }
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            minWidth: 0,
                            px: 2,
                            py: 1.5,
                            bgcolor: isSelected
                              ? "rgb(255, 224, 208)"
                              : sessionListSurfaceColor,
                            // borderTop: "1px solid #E5E7EB",
                            cursor: "pointer",
                            "&:hover": {
                              bgcolor: "rgb(255, 224, 208)",
                            },
                          }}
                        >
                          <Tooltip
                            title={
                              <Box sx={{ py: 0.5 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: "12px",
                                    fontWeight: 400,
                                    color: "white",
                                    display: "block",
                                    mb: 0.5,
                                  }}
                                >
                                  Patrol ID:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: "Roboto Mono, monospace",
                                    fontSize: "12px",
                                    fontWeight: 400,
                                    color: "#e0e0e0",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {id || ""}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: "11px",
                                    fontWeight: 400,
                                    color: "#b0b0b0",
                                    display: "block",
                                    mt: 1,
                                    fontStyle: "italic",
                                  }}
                                >
                                  💡 Click to copy ID
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="top"
                            enterDelay={300}
                            leaveDelay={0}
                            PopperProps={{
                              sx: {
                                "& .MuiTooltip-tooltip": {
                                  bgcolor: "rgba(33, 33, 33, 0.95)",
                                  backdropFilter: "blur(8px)",
                                  border: "1px solid rgba(255, 255, 255, 0.1)",
                                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
                                  maxWidth: "400px",
                                  p: 1.5,
                                },
                                "& .MuiTooltip-arrow": {
                                  color: "rgba(33, 33, 33, 0.95)",
                                },
                              },
                            }}
                          >
                            <Typography
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClick(id, "ID");
                              }}
                              sx={{
                                fontFamily: "Roboto, sans-serif",
                                fontSize: "14px",
                                color: "#4A5565",
                                minWidth: 50, // never smaller than this
                                maxWidth: 140, // never larger than this
                                flexShrink: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                borderRadius: 0.5,
                                px: 0.5,
                                py: 0.25,
                                transition: "all 0.2s ease",
                                "&:hover": true
                                  ? {
                                      bgcolor: "rgba(41, 50, 229, 0.04)",
                                      textDecoration: true
                                        ? "underline dotted"
                                        : "none",
                                      color: "#212121",
                                    }
                                  : {},
                                "&:focus": {
                                  outline: "2px solid #2932E5",
                                  outlineOffset: "2px",
                                },
                                "&:focus:not(:focus-visible)": {
                                  outline: "none",
                                },
                                textUnderlineOffset: "2px",
                                transition: "color 0.2s ease",
                              }}
                            >
                              {displayPatrolId}
                            </Typography>
                          </Tooltip>
                          <Tooltip
                            title={fullDate || date || ""}
                            arrow
                            placement="top"
                            enterDelay={300}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Roboto, sans-serif",
                                fontSize: "14px",
                                color: "#4A5565",
                                minWidth: "150px",
                                maxWidth: "170px",
                                flexShrink: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {date}
                            </Typography>
                          </Tooltip>
                          {isComparePatrol && (
                            <Tooltip
                              title={
                                <Box sx={{ py: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontFamily: "Roboto, sans-serif",
                                      fontSize: "12px",
                                      fontWeight: 400,
                                      color: "white",
                                      display: "block",
                                      mb: 0.5,
                                    }}
                                  >
                                    Full Path:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: "Roboto Mono, monospace",
                                      fontSize: "12px",
                                      fontWeight: 400,
                                      color: "#e0e0e0",
                                      wordBreak: "break-all",
                                    }}
                                  >
                                    {getPathOnly(path || "") || "—"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontFamily: "Roboto, sans-serif",
                                      fontSize: "11px",
                                      fontWeight: 400,
                                      color: "#b0b0b0",
                                      display: "block",
                                      mt: 1,
                                      fontStyle: "italic",
                                    }}
                                  >
                                    💡 Click to copy path
                                  </Typography>
                                </Box>
                              }
                              arrow
                              placement="top"
                              enterDelay={300}
                              leaveDelay={0}
                              PopperProps={{
                                sx: {
                                  "& .MuiTooltip-tooltip": {
                                    bgcolor: "rgba(33, 33, 33, 0.95)",
                                    backdropFilter: "blur(8px)",
                                    border:
                                      "1px solid rgba(255, 255, 255, 0.1)",
                                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
                                    maxWidth: "400px",
                                    p: 1.5,
                                  },
                                  "& .MuiTooltip-arrow": {
                                    color: "rgba(33, 33, 33, 0.95)",
                                  },
                                },
                              }}
                            >
                              <Box
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const dir = getPathOnly(path || "");
                                  if (dir) {
                                    handleClick(dir, "path");
                                  }
                                }}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  minWidth: 0,
                                  flex: "1 1 28%",
                                  maxWidth: "35%",
                                  borderRadius: 0.5,
                                  px: 0.5,
                                  py: 0.25,
                                  cursor: getPathOnly(path || "")
                                    ? "pointer"
                                    : "default",
                                  "&:hover": getPathOnly(path || "")
                                    ? { bgcolor: "rgba(41, 50, 229, 0.04)" }
                                    : {},
                                }}
                              >
                                <FolderIcon
                                  sx={{
                                    fontSize: 18,
                                    color: "#2932e5",
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  sx={{
                                    ...truncateSx,
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: "14px",
                                    color: "#4A5565",
                                  }}
                                >
                                  {truncateText(
                                    getPathOnly(path || "") || "—",
                                    15,
                                  )}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                          <Box
                            onClick={() => handleClick(filename, "name")}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flex: 1,
                              minWidth: 0,
                              borderRadius: 0.5,
                              px: 0.5,
                              py: 0.25,
                              transition: "all 0.2s ease",
                              "&:hover": true
                                ? {
                                    bgcolor:
                                      detectFileType("", filename).isImage &&
                                      "rgba(41, 50, 229, 0.04)",
                                  }
                                : {},
                              "&:focus": {
                                outline: "2px solid #2932E5",
                                outlineOffset: "2px",
                              },
                              "&:focus:not(:focus-visible)": {
                                outline: "none",
                              },
                            }}
                          >
                            {detectFileType("", filename).isImage ? (
                              <ImageIcon
                                sx={{
                                  fontSize: 18,
                                  color: " rgb(76, 175, 80)",
                                  flexShrink: 0,
                                }}
                              />
                            ) : (
                              <PictureAsPdfIcon
                                sx={{
                                  fontSize: 18,
                                  color: " #DC2626",
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <Tooltip
                              title={
                                <Box sx={{ py: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontFamily: "Roboto, sans-serif",
                                      fontSize: "12px",
                                      fontWeight: 400,
                                      color: "white",
                                      display: "block",
                                      mb: 0.5,
                                    }}
                                  >
                                    Full Filename:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: "Roboto Mono, monospace",
                                      fontSize: "12px",
                                      fontWeight: 400,
                                      color: "#e0e0e0",
                                      wordBreak: "break-all",
                                    }}
                                  >
                                    {filename}
                                  </Typography>
                                  {detectFileType("", filename).isImage && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: "Roboto, sans-serif",
                                        fontSize: "11px",
                                        fontWeight: 400,
                                        color: "#b0b0b0",
                                        display: "block",
                                        mt: 1,
                                        fontStyle: "italic",
                                      }}
                                    >
                                      💡 Click to copy filename
                                    </Typography>
                                  )}
                                </Box>
                              }
                              arrow
                              placement="top"
                              enterDelay={300}
                              leaveDelay={0}
                              PopperProps={{
                                sx: {
                                  borderRadius: "10px",
                                  "& .MuiTooltip-tooltip": {
                                    bgcolor: "rgba(33, 33, 33, 0.95)",
                                    backdropFilter: "blur(8px)",
                                    border:
                                      "1px solid rgba(255, 255, 255, 0.1)",
                                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
                                    maxWidth: "400px",
                                    p: 1.5,
                                  },
                                  "& .MuiTooltip-arrow": {
                                    color: "rgba(33, 33, 33, 0.95)",
                                  },
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: "Roboto, sans-serif",
                                  fontSize: "14px",
                                  color: "#4A5565",
                                  ...truncateSx,
                                  flex: 1,
                                  "&:hover": true
                                    ? {
                                        textDecoration:
                                          detectFileType("", filename)
                                            .isImage && true
                                            ? "underline dotted"
                                            : "none",
                                        color: "#212121",
                                      }
                                    : {},
                                  textUnderlineOffset: "2px",
                                  transition: "color 0.2s ease",
                                }}
                              >
                                {truncateText(filename, 23, "path")}
                              </Typography>
                            </Tooltip>
                          </Box>
                          <Typography
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "14px",
                              color: "#4A5565",
                              minWidth: "60px",
                            }}
                          >
                            {sessionName}
                            {/* {session} */}
                          </Typography>
                          {/* <IconButton
            size="small"
            // onClick={(e) => {
            //   e.stopPropagation();
            //   onActionClick?.(patrol, e);
            // }}
            sx={{
              color: "#4A5565",
              "&:hover": {
                bgcolor: "rgb(255, 224, 208)",
              },
            }}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton> */}
                        </Box>
                      </Box>
                    );
                  })}
              </Collapse>
            </Box>
          );
        })}
    </Box>
  );
}
